import { CdkDragDrop, CdkDragMove, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Subject, Subscription, range, takeUntil } from 'rxjs';
import { GetDivisions$Params } from 'src/api/generated/fn/divisions/get-divisions';
import { AddGroup$Params } from 'src/api/generated/fn/group/add-group';
import { GetGroup$Params } from 'src/api/generated/fn/group/get-group';
import { UpdateGroup$Params } from 'src/api/generated/fn/group/update-group';
import { GetMenuCategoriesForVenue$Params } from 'src/api/generated/fn/menu-v-2/get-menu-categories-for-venue';
import { AddGroupDto } from 'src/api/generated/models/Digiwork/MaitreD/Pos/DTO/Group/add-group-dto';
import { GroupDto } from 'src/api/generated/models/Digiwork/MaitreD/Pos/DTO/Group/group-dto';
import { ServingAreaLayoutDto } from 'src/api/generated/models/Digiwork/MaitreD/Pos/DTO/Group/serving-area-layout-dto';
import { ServingAreaMenuCategoryDto } from 'src/api/generated/models/Digiwork/MaitreD/Pos/DTO/Group/serving-area-menu-category-dto';
import { TableDto } from 'src/api/generated/models/Digiwork/MaitreD/Pos/DTO/Group/table-dto';
import { UpdateGroupDto } from 'src/api/generated/models/Digiwork/MaitreD/Pos/DTO/Group/update-group-dto';
import { DivisionDto } from 'src/api/generated/models/Digiwork/MaitreD/Pos/DTO/division-dto';
import { DivisionsService, GroupService, MenuV2Service } from 'src/api/generated/services';
import { TablePictureEnum, TableShapeConfigModel, TableTypeConfigModel } from 'src/app/pos-panel/enums/table-picture.enum';
import { CurrentVenueHelperService } from 'src/app/share-modul/services/current-venue-helper/current-venue.helper.service';

@Component({
  selector: 'app-new-group',
  templateUrl: './new-group.component.html',
  styleUrls: ['./new-group.component.scss']
})
export class NewGroupComponent implements OnInit, OnDestroy {

  venueId: string;
  activeIndex: number = 0;
  servingArea: string;

  group: GroupDto;
  divisions: Array<DivisionDto>;
  menuCategories: Array<ServingAreaMenuCategoryDto> = new Array<ServingAreaMenuCategoryDto>();
  selectedLayout: ServingAreaLayoutDto = {} as ServingAreaLayoutDto;

  edit: boolean = false; 
  tablePictures: Array<TableDto> = new Array<TableDto>();
  totalCapacity: number = 0;
  form: FormGroup;

  unsubscribe: Subject<void> = new Subject<void>();
  public venueIdUpdateSubscription: Subscription;

  public dragedTable: TableDto;
  public startX: string;
  public startY: string;

  constructor(private route: ActivatedRoute,
              public router: Router,
              private menuService: MenuV2Service,
              private divisionsService: DivisionsService,
              private groupService: GroupService,
              private confirmationService: ConfirmationService,
              private messageService: MessageService,
              public currentVenueHelper: CurrentVenueHelperService,
              private formBuilder: FormBuilder,
              private translate: TranslateService) { 
    
    this.form = new FormGroup({
      name: new FormControl('', [Validators.required]),
      categories: this.formBuilder.array([])
    });  
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      if (params['activeIndex']) {
          this.activeIndex = params['activeIndex'];
      }
    });

    this.route.params.subscribe(params => {
      this.venueId = params['venue-id'];
      this.currentVenueHelper.updateCurrentVenueIdDropdown(params['venue-id']);
      
      if(params['serving-area-id']){
        this.edit = true;
        this.servingArea = params['serving-area-id'];
      }
      this.getDivisionsList();  

      if (!this.edit){
        this.getMenuCategoriesForVenue();
        this.group = {} as GroupDto;
        this.group.servingAreMenuCategories = new Array<ServingAreaMenuCategoryDto>();
        this.group.servingAreaLayouts = new Array<ServingAreaLayoutDto>();
        this.group.servingAreaLayouts.push({} as ServingAreaLayoutDto);
        this.group.servingAreaLayouts[0].tables = new Array<TableDto>();
        this.selectedLayout = this.group.servingAreaLayouts[0];
      }else {
        const params: GetGroup$Params = { servingAreaId: this.servingArea };
        this.groupService.getGroup(params).subscribe( response => {
            this.group = response;
            this.getMenuCategoriesForVenue();
            if(this.group.servingAreaLayouts){
              this.selectedLayout = this.group.servingAreaLayouts[0];
              this.countCapacity();
            }
          });
      }  
      Object.entries(TablePictureEnum).forEach((tableType: string[]) => {
        let table = {} as TableDto;
        table.capacity = TableTypeConfigModel[tableType[0] as keyof typeof TableTypeConfigModel].value;
        table.shapeType = TableShapeConfigModel[tableType[0] as keyof typeof TableShapeConfigModel].value.toString();
        this.tablePictures.push(table);
      }); 
    });  
  }

  changeTab(e: any) {
    this.activeIndex = e.index;
    this.router.navigate([],
      {
        relativeTo: this.route,
        queryParams: { activeIndex: this.activeIndex },
        queryParamsHandling: 'merge',
      });
  }

  getItemControls(index: number){
    return (this.categories.controls[index] as FormGroup)
  }

  private createForm(): void {
    this.form = new FormGroup({
      name: new FormControl('', [Validators.required]),
      categories: this.formBuilder.array([])
    });   
    this.menuCategories?.forEach(item => this.categories.push(this.createTableRow()))
  }

  private createEditForm(): void {
    this.form = new FormGroup({
      name: new FormControl(this.group.name, [Validators.required]),
      categories: this.formBuilder.array([])
    });   
    this.menuCategories?.forEach(item => this.categories.push(this.createPopulatedTableRow(item)))
  }

  get categories(): FormArray {
    return this.form.get('categories') as FormArray;
  }
  
  private createTableRow(): FormGroup {
    return this.formBuilder.group({
      menuCategory: new FormControl(undefined, {
        validators: []
      }),
     division: new FormControl(undefined, {
        validators: []
      })
    });
  }

  private createPopulatedTableRow(item: any): FormGroup {
    if(item.division == undefined){
      return this.createTableRow();
    }else{
      return this.formBuilder.group({
        menuCategory: new FormControl(item.menuCategory, {
          validators: []
        }),
      division: new FormControl(item.division, {
          validators: []
        })
      });
    }
  }

  selectRow(event: any){
    let index = event.index;

    this.getItemControls(index).controls['menuCategory'].setValue(event.data.menuCategory);
    this.getItemControls(index).controls['menuCategory'].updateValueAndValidity();

    this.getItemControls(index).controls['division'].setValidators(Validators.required);
    this.getItemControls(index).controls['division'].updateValueAndValidity();

  }

  unselectRow(event: any){
    let index = event.index;

    const menuCategoryIndex = this.menuCategories.findIndex(category => category.menuCategoryId == event.data.menuCategoryId);
    this.menuCategories[menuCategoryIndex].division = {};

    this.getItemControls(index).controls['menuCategory'].setValue(undefined);
    this.getItemControls(index).controls['menuCategory'].updateValueAndValidity();

    this.getItemControls(index).controls['division'].setValue(undefined);
    this.getItemControls(index).controls['division'].removeValidators(Validators.required);
    this.getItemControls(index).controls['division'].updateValueAndValidity();
  }

  getDivisionsList(){
    const params: GetDivisions$Params = { venueId: this.venueId, isRemoved: false };
    this.divisionsService.getDivisions(params).subscribe(response => {
      if(response.divisions != undefined){
        this.divisions = response.divisions;
      }
    })
  }

  getMenuCategoriesForVenue() {
    var params: GetMenuCategoriesForVenue$Params = {} as GetMenuCategoriesForVenue$Params;
    params.venueId = this.venueId;
    params.isRemoved = false;
    params.isMenuSubcategoryRemoved = false;
    this.menuService.getMenuCategoriesForVenue(params)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (response) => {
          if (response.menuCategories != undefined) {
            if(this.edit){
              this.menuCategories = new Array<ServingAreaMenuCategoryDto>();
              response.menuCategories.forEach(menuCategory => {
                  const div = this.group.servingAreMenuCategories?.find(item => item.menuCategoryId == menuCategory.id)?.division;
                  this.menuCategories.push(div ? { menuCategory: menuCategory, menuCategoryId: menuCategory.id, division: div, divisionId: div.id } as ServingAreaMenuCategoryDto : { menuCategory: menuCategory, menuCategoryId: menuCategory.id } as ServingAreaMenuCategoryDto);
                });
              this.createEditForm();
            }else{
              this.menuCategories = response.menuCategories.map(category => ({menuCategory: category, menuCategoryId: category.id } as ServingAreaMenuCategoryDto));
              this.createForm();
            }
          }
        },
        error: errorResponse => {
        }
      });
  }

  createOrEditGroup(){
    if(this.edit){
      if(this.form.valid){
        this.confirmationService.confirm({
          header: this.translate.instant('GROUPS.UPDATE_GROUP_CONFIRMATION_TITLE'),
          message: this.translate.instant('GROUPS.UPDATE_GROUP_CONFIRMATION_TEXT'),
          acceptLabel: this.translate.instant('YES'),
          rejectLabel: this.translate.instant('NO'),
          defaultFocus: "reject",
          acceptButtonStyleClass: 'soc-confirmation-dialog dialog-btn constructive-btn',
          rejectButtonStyleClass: 'soc-confirmation-dialog dialog-btn reject-btn',
          accept: () => {
            const updateDto: UpdateGroupDto = { name: this.form.getRawValue().name, servingAreaMenuCategories:  this.form.getRawValue().categories.filter((c: any) => c.menuCategory != undefined), servingAreaLayout: this.group.servingAreaLayouts };  
            const params: UpdateGroup$Params = { servingAreaId: this.servingArea, body: updateDto };
            this.groupService.updateGroup(params).subscribe({
              next: response => {
                this.messageService.add({ severity: 'success', summary: 'The group was successfully updated!', life: 3000 });
                this.router.navigate([this.venueId, 'groups']);
              },
              error: errorResponse => this.messageService.add({ severity: 'error', summary: 'Error occurred!', life: 3000 })
            });
          
          }
        });
      }
    }else {
      if(this.form.valid){
        this.confirmationService.confirm({
          header: this.translate.instant('GROUPS.CREATE_CONFIRMATION_TITLE'),
          message: this.translate.instant('GROUPS.CREATE_CONFIRMATION_TEXT'),
          acceptLabel: this.translate.instant('YES'),
          rejectLabel: this.translate.instant('NO'),
          defaultFocus: "reject",
          acceptButtonStyleClass: 'soc-confirmation-dialog dialog-btn constructive-btn',
          rejectButtonStyleClass: 'soc-confirmation-dialog dialog-btn reject-btn',
          accept: () => {
            if(this.group.servingAreaLayouts){
              this.group.servingAreaLayouts[0].name = "";
            }              
            const addGroupDto: AddGroupDto = { name: this.form.getRawValue().name , servingAreaLayout: this.group.servingAreaLayouts, servingAreaMenuCategories: this.form.getRawValue().categories.filter((c: any) => c.menuCategory != undefined), venueId: this.venueId };
            const params: AddGroup$Params = { body: addGroupDto };
            this.groupService.addGroup(params).subscribe( response => this.router.navigate([this.venueId, 'groups']));
          }
        });
      }
    }
  }

  navigateToMain(){
    this.router.navigate([this.venueId, 'groups'])
  }

  dragStart(e: any, t: any) {
    this.startX = e.offsetX;
    this.startY = e.offsetY;
    this.dragedTable ={} as TableDto;
    this.dragedTable = t;
    this.confirmationService.close();
  }

  dragEnd(e: any) {

  }

  mouseDown(e: any, table: any){
        this.confirmationService.confirm({
          header: this.translate.instant('GROUPS.DELETE_TABLE_TITLE'),
          message: this.translate.instant('GROUPS.DELETE_TABLE_TEXT') + table.name + "?",
          acceptLabel: this.translate.instant('YES'),
          rejectLabel: this.translate.instant('NO'),
          defaultFocus: 'reject',
          acceptButtonStyleClass: 'soc-confirmation-dialog dialog-btn destructive-btn',
          rejectButtonStyleClass: 'soc-confirmation-dialog dialog-btn reject-btn',
          accept: () => {
            if (table.id && this.selectedLayout.tables?.find(t => t.id === table.id)) {
              this.selectedLayout.tables.forEach(t => {
                if(t.name == table.name){
                  t.isRemoved = true;
                }
              })
            }else if (table.name != undefined && this.selectedLayout.tables?.find(t => t.name == table.name)) {
              this.selectedLayout.tables.forEach(t => {
                if(t.name == table.name){
                  t.isRemoved = true;
                }
              })
            }
            this.countCapacity();
          }
        });
  }

  drop(e: any) {
    
  if (this.dragedTable) {
      let table = {} as TableDto;
      table.shapeType = this.dragedTable.shapeType;
      table.statusType = this.dragedTable.statusType;
      table.capacity = this.dragedTable.capacity;
      table.name = this.dragedTable.name;
      if(this.dragedTable.isRemoved != undefined){
        table.isRemoved = this.dragedTable.isRemoved;
      }
      if(this.dragedTable.id != undefined){
        table.id = this.dragedTable.id;
      }

      if(!this.selectedLayout.tables){
        this.selectedLayout.tables = new Array<TableDto>();
      }

      if(!table.name){
        const tableNumbers = this.selectedLayout.tables.map(table => table.name?.slice(1));
        range(1, this.selectedLayout.tables.length+1).forEach(t => {
          if(!tableNumbers.includes(t.toString())){
            table.name = "T".concat(t.toString());
            return;
          }
        })
      }

      let top = ((parseInt(e.layerY)) - parseInt(this.startY));
      let left = ((parseInt(e.layerX)) - parseInt(this.startX));

      if ((top >= 330 && top <= 780) && (left >= 292 && left <= 1524)) {
        table.positionY = top; 
        table.positionX = left;
        
        if(table.id != undefined && this.selectedLayout.tables.find(t => t.id == table.id)){
          this.selectedLayout.tables = this.selectedLayout.tables.filter(t => t.id != table.id);
        } else if (table.name != undefined && this.selectedLayout.tables.find(t => t.name == table.name)) {
          this.selectedLayout.tables = this.selectedLayout.tables.filter(t => t.name != table.name);
        }
        this.selectedLayout.tables?.push(table);
        this.countCapacity();
      }
      this.dragedTable = {} as TableDto;
    }
  }

  getTablePicture(capacity: number | undefined){
      return TablePictureEnum[ "Table" + capacity as keyof typeof TablePictureEnum];
  }

  @ViewChild('dropZone', { read: ElementRef, static: true })
  dropZone: ElementRef;

  _currentIndex: any;
  _currentField: any;
  isPointerOverContainer: boolean;
  _pointerPosition: { x: number; y: number; };

  moved(event: CdkDragMove) {
    this._pointerPosition = event.pointerPosition;
  }

  itemDropped(event: CdkDragDrop<any[]>) {

    if (event.previousContainer === event.container) {
      if(!this.selectedLayout.tables){
        this.selectedLayout.tables = new Array<TableDto>();
      }
      moveItemInArray(this.selectedLayout.tables, event.previousIndex, event.currentIndex);
      
    } else {
      event.item.data.yPosition =
        this._pointerPosition.y -
        this.dropZone.nativeElement.getBoundingClientRect().top +
        'px';
      event.item.data.xPosition =
        this._pointerPosition.x -
        this.dropZone.nativeElement.getBoundingClientRect().left +
        'px';
      this.addField({ ...event.item.data }, event.currentIndex);
    }
  }

  addField(fieldType: any, index: number) {
    this.selectedLayout.tables?.splice(index, 0, fieldType)
  }

  changePosition(event: CdkDragDrop<any>, field: any) {

    const rectZone = this.dropZone.nativeElement.getBoundingClientRect()
    const rectElement = event.item.element.nativeElement.getBoundingClientRect()

    let yPosition =+ field.yPosition + event.distance.y
    let xPosition =+ field.xPosition + event.distance.x

    const out=yPosition<0 || xPosition<0 || (yPosition>(rectZone.height-rectElement.height)) || (xPosition>(rectZone.width-rectElement.width))
    if (!out){

       field.yPosition = yPosition;
       field.xPosition = xPosition;
    }
    else{
       this.selectedLayout.tables=this.selectedLayout.tables?.filter(x => x != field)
    }
  }

  countCapacity() {
    this.totalCapacity = 0;
    this.selectedLayout.tables?.forEach(table => { 
      if(table.capacity) {
        this.totalCapacity += table.capacity;
      }
    })
  }

  public ngOnDestroy(): void {
    if (this.venueIdUpdateSubscription) {
      this.venueIdUpdateSubscription.unsubscribe();
    }
  }

}
